import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  PencilIcon,
  DocumentArrowDownIcon,
  PrinterIcon, // Add PrinterIcon
  ClipboardIcon,
  ArrowPathIcon
} from '@heroicons/react/24/outline';
import axios from '../../api/axiosConfig';
import { useAuth } from '../../contexts/AuthContext';
import PageHeader from '../../components/common/PageHeader';
import LoadingSpinner from '../../components/common/LoadingSpinner';

interface Client {
  id: number;
  name: string;
  phone: string;
  email: string;
  address: string;
  bin?: string;
  // Add other fields if needed from Client model
  taxId?: string;
  iik?: string;
  bankName?: string;
  bik?: string;
  director?: string;
}

// Add Requisites interface
interface Requisites {
    name?: string;
    address?: string;
    bin?: string;
    iik?: string;
    bankName?: string;
    bik?: string;
    phone?: string;
    email?: string;
    basisOfAuthority?: string;
    director?: string;
}

interface ContractAppendix {
  id: number;
  contractId: number;
  orderId: number;
  appendixNumber: string;
  appendixDate: string;
  totalAmount: number;
  createdAt: string;
  Order?: {
    id: number;
    orderNumber: string;
    totalAmount: number;
    finalAmount: number;
  };
}

interface Contract {
  id: number;
  clientId: number;
  contractNumber: string;
  issueDate: string;
  startDate: string;
  endDate: string;
  signDate?: string;
  type?: string;
  title?: string;
  status: 'draft' | 'sent' | 'signed' | 'active' | 'completed' | 'terminated';
  termsAndConditions?: string;
  paymentTerms?: string;
  notes?: string;
  createdAt: string;
  updatedAt: string;
  Client?: Client; // Keep for basic client info link
  Appendices?: ContractAppendix[];
  Orders?: {
    id: number;
    orderNumber: string;
  }[];
  // Add new requisite fields
  companyRequisites?: Requisites;
  clientRequisites?: Requisites;
}

// Use the same status mapping as in the list/form for consistency
const CONTRACT_STATUSES_MAP: { [key: string]: string } = {
  draft: 'Черновик',
  sent: 'Отправлен',
  signed: 'Подписан',
  active: 'Активный',
  completed: 'Завершен',
  terminated: 'Расторгнут'
};

const ContractDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { can } = useAuth();
  const [contract, setContract] = useState<Contract | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  useEffect(() => {
    const fetchContractData = async () => {
      setLoading(true);
      setError(null);
      
      try {
        // Fetch contract details with related entities
        const response = await axios.get(`/contracts/${id}`);
        setContract(response.data);
      } catch (err) {
        console.error('Error fetching contract data:', err);
        setError('Ошибка при загрузке данных договора');
      } finally {
        setLoading(false);
      }
    };
    
    fetchContractData();
  }, [id]);

  // Open printable view in a new tab
  const handleOpenPrintView = () => {
    if (!contract) return;
    // Construct the URL for the print page
    const printUrl = `/contracts/${contract.id}/print`;
    // Open the URL in a new tab
    const printWindow = window.open(printUrl, '_blank');
    if (!printWindow) {
      alert('Не удалось открыть окно для печати. Пожалуйста, проверьте настройки блокировки всплывающих окон.');
    }
  };

  // Open printable view for Appendix
  const handleOpenAppendixPrintView = (contractId: number | undefined /*, appendixId: number */) => {
    // Note: The current backend route uses contractId. If specific appendix printing is needed,
    // the backend route/controller and this function would need adjustment to use appendixId.
    if (!contractId) {
        alert('Ошибка: ID договора не найден для печати приложения.');
        return;
    };
    // Construct the URL for the appendix print page using contract ID
    const printUrl = `/contracts/${contractId}/appendix-print-data`;
    // Open the URL in a new tab
    const printWindow = window.open(printUrl, '_blank');
    if (!printWindow) {
      alert('Не удалось открыть окно для печати приложения. Пожалуйста, проверьте настройки блокировки всплывающих окон.');
    }
  };

  // Update contract status
  const handleUpdateStatus = async (newStatus: Contract['status']) => {
    if (!contract) return;
    
    try {
      await axios.put(`/contracts/${id}/status`, { status: newStatus });
      
      // Update local contract state
      setContract(prevContract => {
        if (!prevContract) return null;
        return { ...prevContract, status: newStatus };
      });
    } catch (error) {
      console.error('Error updating contract status:', error);
      alert('Ошибка при обновлении статуса договора');
    }
  };
  
  const formatDate = (dateString: string | undefined) => {
    if (!dateString) return '-';
    
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('ru-RU', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    }).format(date);
  };
  
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('ru-KZ', {
      style: 'currency',
      currency: 'KZT',
      maximumFractionDigits: 2
    }).format(amount);
  };
  
  const getStatusBadgeClass = (status: string) => {
    switch (status) {
      case 'draft':
        return 'bg-gray-100 text-gray-800';
      case 'sent':
        return 'bg-blue-100 text-blue-800';
      case 'signed':
        return 'bg-green-100 text-green-800';
      case 'active':
        return 'bg-purple-100 text-purple-800';
      case 'completed':
        return 'bg-gray-100 text-gray-800';
      case 'terminated':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };
  
  const getStatusText = (status: string) => {
    return CONTRACT_STATUSES_MAP[status] || status;
  };
  
  if (loading) {
    return <LoadingSpinner />;
  }
  
  if (error || !contract) {
    return (
      <div className="bg-white rounded-lg shadow p-6 text-center">
        <p className="text-red-600">{error || 'Договор не найден'}</p>
        <Link to="/contracts" className="mt-4 btn-outline inline-block">
          Вернуться к списку договоров
        </Link>
      </div>
    );
  }
  
  return (
    <div>
      <PageHeader
        title={contract.title || `Договор №${contract.contractNumber}`}
        subtitle={contract.Client ? `Клиент: ${contract.Client.name}` : ''}
        backLink="/contracts"
        actions={
          <>
            <button
              onClick={handleOpenPrintView} // Use new handler
              className="btn-secondary flex items-center"
            >
              <PrinterIcon className="h-5 w-5 mr-1" /> {/* Use PrinterIcon */}
              Печать
            </button>

            {can('contracts.edit') && // Changed from isAllowed
             (contract.status === 'draft' || contract.status === 'sent') && (
              <Link
                to={`/contracts/edit/${contract.id}`}
                className="btn-primary flex items-center"
              >
                <PencilIcon className="h-5 w-5 mr-1" />
                Редактировать
              </Link>
            )}
          </>
        }
      />
      
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
        {/* Contract information */}
        <div className="lg:col-span-2 bg-white rounded-lg shadow p-6 space-y-6">
          {/* Header section with status */}
          <div className="flex flex-col sm:flex-row justify-between items-start">
            <div>
              <h2 className="text-xl font-bold text-gray-900">Информация о договоре</h2>
               {/* Display Title if available */}
               {contract.title && (
                    <p className="text-lg text-gray-600 mt-1">{contract.title}</p>
               )}
               <div className="text-sm text-gray-500 mt-1">
                  № {contract.contractNumber} | Тип: {contract.type || '-'} | Создан: {formatDate(contract.createdAt)}
               </div>
            </div>
            <div className="flex items-center space-x-2 mt-3 sm:mt-0">
              <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getStatusBadgeClass(contract.status)}`}>
                {getStatusText(contract.status)}
              </span>
              
              {/* Status update dropdown placeholder - logic exists below */}
              {can('contracts.manage_status') && (
                <div className="relative">
                  {/* Button to open status dropdown - TODO: Implement Dropdown component */}
                  <button
                    type="button"
                    className="btn-outline-sm p-1 rounded-md hover:bg-gray-100"
                    title="Изменить статус"
                    onClick={() => alert('Dropdown for status change needed')} // Placeholder action
                  >
                     <ArrowPathIcon className="h-4 w-4" />
                  </button>
                   {/* TODO: Replace with actual dropdown logic 
                   <div className="absolute right-0 mt-2 w-48 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-10">
                       <div className="py-1">
                           {Object.entries(CONTRACT_STATUSES_MAP).map(([key, value]) => (
                               <button 
                                   key={key}
                                   onClick={() => handleUpdateStatus(key as Contract['status'])}
                                   className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 w-full text-left disabled:opacity-50"
                                   disabled={key === contract.status}
                               >
                                   {value}
                               </button>
                           ))}
                       </div>
                   </div>
                   */}
                </div>
              )}
            </div>
          </div>
          
           {/* Dates Section */}
           <div className="border-t pt-4">
               <h3 className="text-base font-medium text-gray-700 mb-3">Ключевые даты</h3>
               <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
                    <div>
                         <div className="text-gray-500">Составлен</div>
                         <div className="text-gray-900 font-medium">{formatDate(contract.issueDate)}</div>
                     </div>
                     <div>
                         <div className="text-gray-500">Начало</div>
                         <div className="text-gray-900 font-medium">{formatDate(contract.startDate)}</div>
                     </div>
                     <div>
                         <div className="text-gray-500">Окончание</div>
                         <div className="text-gray-900 font-medium">{formatDate(contract.endDate)}</div>
                     </div>
                    <div>
                         <div className="text-gray-500">Подписан</div>
                         <div className="text-gray-900 font-medium">{formatDate(contract.signDate)}</div>
                     </div>
              </div>
           </div>

           {/* Requisites Section */}
           <div className="border-t pt-4 grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Company Requisites */}
                <div>
                    <h3 className="text-base font-medium text-gray-700 mb-3">Реквизиты Исполнителя</h3>
                    {contract.companyRequisites ? (
                        <dl className="text-sm space-y-1">
                            {contract.companyRequisites.name && <><dt className="text-gray-500">Название:</dt><dd className="text-gray-900">{contract.companyRequisites.name}</dd></>}
                            {contract.companyRequisites.address && <><dt className="text-gray-500">Адрес:</dt><dd className="text-gray-900">{contract.companyRequisites.address}</dd></>}
                            {contract.companyRequisites.bin && <><dt className="text-gray-500">БИН/ИИН:</dt><dd className="text-gray-900">{contract.companyRequisites.bin}</dd></>}
                            {contract.companyRequisites.iik && <><dt className="text-gray-500">ИИК:</dt><dd className="text-gray-900">{contract.companyRequisites.iik}</dd></>}
                            {contract.companyRequisites.bankName && <><dt className="text-gray-500">Банк:</dt><dd className="text-gray-900">{contract.companyRequisites.bankName}</dd></>}
                            {contract.companyRequisites.bik && <><dt className="text-gray-500">БИК:</dt><dd className="text-gray-900">{contract.companyRequisites.bik}</dd></>}
                            {contract.companyRequisites.phone && <><dt className="text-gray-500">Телефон:</dt><dd className="text-gray-900">{contract.companyRequisites.phone}</dd></>}
                            {contract.companyRequisites.email && <><dt className="text-gray-500">Email:</dt><dd className="text-gray-900">{contract.companyRequisites.email}</dd></>}
                            {contract.companyRequisites.director && <><dt className="text-gray-500">Директор:</dt><dd className="text-gray-900">{contract.companyRequisites.director}</dd></>}
                            {contract.companyRequisites.basisOfAuthority && <><dt className="text-gray-500">Основание:</dt><dd className="text-gray-900">{contract.companyRequisites.basisOfAuthority}</dd></>}
                        </dl>
                    ) : (
                        <p className="text-sm text-gray-500 italic">Реквизиты компании не указаны.</p>
                    )}
                </div>
                {/* Client Requisites */}
                <div>
                    <h3 className="text-base font-medium text-gray-700 mb-3">Реквизиты Заказчика</h3>
                     {contract.clientRequisites ? (
                        <dl className="text-sm space-y-1">
                            {contract.clientRequisites.name && <><dt className="text-gray-500">Название:</dt><dd className="text-gray-900">{contract.clientRequisites.name}</dd></>}
                            {contract.clientRequisites.address && <><dt className="text-gray-500">Адрес:</dt><dd className="text-gray-900">{contract.clientRequisites.address}</dd></>}
                            {contract.clientRequisites.bin && <><dt className="text-gray-500">БИН/ИИН:</dt><dd className="text-gray-900">{contract.clientRequisites.bin}</dd></>}
                            {contract.clientRequisites.iik && <><dt className="text-gray-500">ИИК:</dt><dd className="text-gray-900">{contract.clientRequisites.iik}</dd></>}
                            {contract.clientRequisites.bankName && <><dt className="text-gray-500">Банк:</dt><dd className="text-gray-900">{contract.clientRequisites.bankName}</dd></>}
                            {contract.clientRequisites.bik && <><dt className="text-gray-500">БИК:</dt><dd className="text-gray-900">{contract.clientRequisites.bik}</dd></>}
                            {contract.clientRequisites.phone && <><dt className="text-gray-500">Телефон:</dt><dd className="text-gray-900">{contract.clientRequisites.phone}</dd></>}
                            {contract.clientRequisites.email && <><dt className="text-gray-500">Email:</dt><dd className="text-gray-900">{contract.clientRequisites.email}</dd></>}
                            {contract.clientRequisites.director && <><dt className="text-gray-500">Директор:</dt><dd className="text-gray-900">{contract.clientRequisites.director}</dd></>}
                        </dl>
                    ) : (
                        <p className="text-sm text-gray-500 italic">Реквизиты клиента не указаны.</p>
                    )}
                </div>
           </div>

            {/* Payment Terms and Notes Section */}
             <div className="border-t pt-4">
                 <h3 className="text-base font-medium text-gray-700 mb-3">Условия и Примечания</h3>
                 <div className="space-y-3 text-sm">
                     {contract.paymentTerms && (
                        <div>
                             <div className="text-gray-500">Условия оплаты</div>
                             <p className="text-gray-900 whitespace-pre-wrap">{contract.paymentTerms}</p>
                        </div>
                    )}
                    {contract.notes && (
                        <div>
                             <div className="text-gray-500">Примечания</div>
                             <p className="text-gray-900 whitespace-pre-wrap">{contract.notes}</p>
                         </div>
                     )}
                     {contract.termsAndConditions && (
                        <div>
                             <div className="text-gray-500">Прочие Условия (termsAndConditions)</div>
                             <p className="text-gray-900 whitespace-pre-wrap">{contract.termsAndConditions}</p>
                         </div>
                     )}
                     {!contract.paymentTerms && !contract.notes && !contract.termsAndConditions && (
                        <p className="text-gray-500 italic">Дополнительные условия или примечания отсутствуют.</p>
                     )}
                 </div>
             </div>

        </div>

        {/* Client and Related Info */}
        <div className="space-y-6">
          {/* Client Details */}
          {contract.Client && (
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-3">Клиент</h3>
              <div className="text-sm space-y-2">
                 <div>
                    <dt className="text-gray-500">Клиент:</dt>
                    <dd className="font-medium text-gray-800">
                        <Link to={`/clients/${contract.clientId}`} className="text-primary hover:underline">
                            {contract.Client.name}
                        </Link>
                    </dd>
                 </div>
                 {contract.Client.bin && <div><span className="text-gray-500">БИН/ИИН: </span><span className="text-gray-900">{contract.Client.bin}</span></div>}
                 {contract.Client.director && <div><span className="text-gray-500">Директор: </span><span className="text-gray-900">{contract.Client.director}</span></div>}
                 {contract.Client.phone && <div><span className="text-gray-500">Телефон: </span><span className="text-gray-900">{contract.Client.phone}</span></div>}
                 {contract.Client.email && <div><span className="text-gray-500">Email: </span><span className="text-gray-900">{contract.Client.email}</span></div>}
                  {contract.Client.address && <div><span className="text-gray-500">Адрес: </span><span className="text-gray-900">{contract.Client.address}</span></div>}
               </div>
            </div>
          )}
          
          {/* Related Orders (if directly linked) */}
           {contract.Orders && contract.Orders.length > 0 && (
                <div className="bg-white rounded-lg shadow p-6">
                 <h3 className="text-lg font-medium text-gray-900 mb-3">Связанные Заказы</h3>
                 <ul className="list-disc list-inside space-y-1">
                    {contract.Orders.map(order => (
                        <li key={order.id}>
                            <Link to={`/orders/${order.id}`} className="text-sm text-primary hover:text-primary-dark">
                                Заказ №{order.orderNumber}
                            </Link>
                        </li>
                    ))}
                 </ul>
             </div>
           )}
          
        </div>
      </div>
      
      {/* Contract Appendices */}
      {contract.Appendices && contract.Appendices.length > 0 && (
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-xl font-bold text-gray-900 mb-4">Приложения к договору</h2>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Номер
                  </th>
                  <th scope="col" className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Дата
                  </th>
                  <th scope="col" className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Заказ
                  </th>
                  <th scope="col" className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Сумма
                  </th>
                  <th scope="col" className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Действия
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {contract.Appendices.map((appendix) => (
                  <tr key={appendix.id}>
                    <td className="px-4 py-2 text-sm font-medium text-gray-900">
                      {appendix.appendixNumber}
                    </td>
                    <td className="px-4 py-2 text-sm text-gray-700">
                      {formatDate(appendix.appendixDate)}
                    </td>
                    <td className="px-4 py-2 text-sm text-gray-700">
                      {appendix.Order ? (
                        <Link to={`/orders/${appendix.orderId}`} className="text-primary hover:text-primary-dark">
                          Заказ №{appendix.Order.orderNumber}
                        </Link>
                      ) : `ID Заказа: ${appendix.orderId}`}
                    </td>
                    <td className="px-4 py-2 text-sm text-gray-700">
                      {formatCurrency(appendix.totalAmount)}
                    </td>
                    <td className="px-4 py-2 text-right">
                      <button
                        onClick={() => handleOpenAppendixPrintView(contract?.id /*, appendix.id */)} // Pass contract ID
                        className="text-blue-600 hover:text-blue-900"
                        title="Печать приложения" // Update title
                      >
                        <PrinterIcon className="h-5 w-5" /> {/* Change icon */}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default ContractDetails;
